import { useState, useEffect, useRef } from 'react';
import general from '../../utils/general';
import { translations, LanguageKeys } from './translation';
import { studyTips } from './studyTips';
import moment from 'moment-timezone';

export const useController = () => {
    const [currentLanguage, setCurrentLanguage] = useState<LanguageKeys>(general?.getAccount()?.preferredLanguage ?? 'EN');
    const [page, setPage] = useState<string>('main');
    const [choosingOptions, setChoosingOptions] = useState(true);
    const [inputForAi, setInputForAi] = useState('');
    const [betterExplaining, setBetterExplaining] = useState("");
    const [betterExplainingTerm, setBetterExplainingTerm] = useState("");
    const [betterExplainingActive, setBetterExplainingActive] = useState(false);
    const [simpleLoading, setSimpleLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [studyTime, setStudyTime] = useState(0);
    const [sessionSearchedTopics, setSessionSearchedTopics] = useState<string[]>([]);
    const [curiosities, setCuriosities] = useState<string[]>([]);
    const [session, setSession] = useState<any>(undefined);
    const [sessionId, setSessionId] = useState<string>('');
    const [currentTopic, setCurrentTopic] = useState<any>();
    const [youtubeVideos, setYoutubeVideos] = useState<any[]>([]);
    const [currentYoutubeVideo, setCurrentYoutubeVideo] = useState<any>(undefined);
    const [youtubeVideoModal, setYoutubeVideoModal] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState<any[]>([]);
    const [quiz, setQuiz] = useState<any>(undefined);
    const [flashcards, setFlashcards] = useState<any>(undefined);
    const [premiumPopUpActive, setPremiumPopUpActive] = useState<boolean>(false);
    const [currentQuizQuestionIndex, setCurrentQuizQuestionIndex] = useState<number>(0)
    const [redirectLoading, setRedirectLoading] = useState<boolean>(false);
    const [buttonRedirectLoading, setButtonRedirectLoading] = useState<boolean>(false);
    const allLanguageCodes = general.languages.map((lang) => lang.code) as LanguageKeys[];
    const studyTimeRef = useRef<any>(studyTime);
    const [allSessions, setAllSessions] = useState<any>({
        pages: 0,
        currentPage: 1,
        sessions: [],
    });
    const [allQuizzes, setAllQuizzes] = useState<any>({
        pages: 0,
        currentPage: 1,
        quizzes: [],
    });
    const [allFlashcards, setAllFlashcards] = useState<any>({
        pages: 0,
        currentPage: 1,
        flashcards: [],
    });
    const [showSections, setShowSections] = useState({
        terms: true,
        relatedTopics: true,
        sessionTopics: true,
        quizzes: true,
        flashcards: true
    });
    const clearAll = () => {
        setSessionSearchedTopics([]);
        setBetterExplainingActive(false);
        setBetterExplainingTerm("");
        setBetterExplaining("");
        setSimpleLoading(false);
        setSessionId('');
        setSession(undefined);
        setStudyTime(0);
        setInputForAi('');
        setCurrentTopic(undefined);
        setLoading(false);
    }

    /* AI Requests */
    const topicLearn = async (inputSearch?: string) => {
        if (!general.canRequestAI()) {
            general.toast.error(translations[currentLanguage].reachedLimitMessage)
            setPremiumPopUpActive(true);
            return;
        }
        setLoading(true);
        try {
            if (!inputSearch && !inputForAi) {
                general.toast.error('Digite algo para aprender.');
                setLoading(false);
                setTimeout(() => {
                    setPage('studySession');
                    setChoosingOptions(false);
                }, 150);
                return;
            }
            const response = await general.api.post('/ai/topic/learn', {
                prompt: inputSearch ?? inputForAi,
                searchedTopics: sessionSearchedTopics,
                sessionId: sessionId,
            });
            if (response?.data?.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setLoading(false);
                return;
            }
            setSessionSearchedTopics([...sessionSearchedTopics, response.data?.data?.topic?.name ?? '']);
            setCurrentTopic(response.data?.data?.topic ?? undefined);
            setSession((session: any) => (response.data?.data?.session ?? undefined));
            setInputForAi('');
            setLoading(false);
        }
        catch (err) {
            console.log(err);
            setLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const betterExplainTerm = async (inputSearch: string, id: string) => {
        setSimpleLoading(true);
        try {
            if (currentTopic?.searchTerms?.filter((term: any) => term.id === id)[0]?.explanation) {
                setBetterExplaining(currentTopic?.searchTerms?.filter((term: any) => term.id === id)[0]?.explanation);
                setBetterExplainingActive(true);
                setSimpleLoading(false);
                return;
            }
            const response = await general.api.post('/ai/explain/term', {
                title: currentTopic?.name ?? 'Não encontrado',
                prompt: inputSearch,
                sessionId: sessionId,
                topicId: currentTopic?._id,
            });
            if (response.data?.status === 9999) {
                setSimpleLoading(false);
                general.toast.error(translations[currentLanguage].reachedLimitMessage)
                setPremiumPopUpActive(true);
                return;
            }
            if (response.data?.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                setLoading(false);
                return;
            }
            setBetterExplaining(response.data?.data?.termExplanation ?? '');
            setBetterExplainingActive(true);
            setCurrentTopic(response.data?.data?.topic ?? undefined);
            setSession(response.data?.data?.session ?? undefined);
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const getTopicsTips = async () => {
        if (!general.canRequestAI()) {
            general.toast.error(translations[currentLanguage].reachedLimitMessage)
            setPremiumPopUpActive(true);
            return;
        }
        try {
            if (sessionSearchedTopics?.length > 0 && currentTopic?.curiosities?.length <= 1) {
                const response = await general.api.post('/ai/topic/tips', {
                    prompt: sessionSearchedTopics[sessionSearchedTopics.length - 1],
                    sessionId: sessionId,
                    topicId: currentTopic?._id,
                });
                if (response.data.status !== 200) {
                    general.toast.error('Erro ao realizar processo.');
                    setLoading(false);
                    return;
                }
                setCuriosities([...curiosities, ...response.data.data?.tips ?? '']);
                setCurrentTopic(session?.topics?.filter((topic: any) => topic._id === currentTopic?._id)[0]);
            }
        }
        catch (err) {
            console.log(err);
            setLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const createFlashcards = async () => {
        if (!general.canRequestAI()) {
            general.toast.error(translations[currentLanguage].reachedLimitMessage)
            setPremiumPopUpActive(true);
            return;
        }
        if (selectedTopics?.length == 0) {
            general.toast.error(translations[currentLanguage].selectTopicsError);
            return;
        }
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/ai/create/flashcards', {
                sessionId: sessionId,
                topics: selectedTopics
            });
            if (response.data.status !== 200) {
                general.toast.error(translations[currentLanguage].error);
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setFlashcards(response.data.data.flashcards);
            setSession(response.data.data.session);
            setPage('showFlashcards');
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error(translations[currentLanguage].error);
        }
    }

    const createQuiz = async (quizAbout?: string) => {
        if (!general.canRequestAI()) {
            general.toast.error(translations[currentLanguage].reachedLimitMessage)
            setPremiumPopUpActive(true);
            return;
        }
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/ai/create/quiz', {
                sessionId: sessionId,
                topics: quizAbout ?? selectedTopics
            });
            if (response.data.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setQuiz(response.data.data.quiz);
            setCurrentQuizQuestionIndex(0);
            setPage('takingQuiz');
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    /* End AI Requests */

    const updateAllSessions = (page: number) => {
        setSimpleLoading(true);
        try {
            general.api.post('/study/session/list', {
                currentPage: page,
                pageSize: 15,
            }).then((response) => {
                if (response.data.status !== 200) {
                    general.toast.error(translations[currentLanguage].error);
                    setSimpleLoading(false);
                    return;
                }
                setAllSessions({
                    pages: response.data.data.pagesAmount,
                    currentPage: response.data.data.currentPage,
                    sessions: response.data.data.allSessions
                });
                setSimpleLoading(false);
            })
        }
        catch (err) {
            console.log(err);
            general.toast.error(translations[currentLanguage].error);
            setSimpleLoading(false);
        }
    }

    const updateAllQuizzes = (page: number) => {
        setSimpleLoading(true);
        try {
            general.api.post('/quiz/list', {
                currentPage: page,
                pageSize: 15,
            }).then((response) => {
                if (response.data.status !== 200) {
                    general.toast.error(translations[currentLanguage].error);
                    setSimpleLoading(false);
                    return;
                }
                setAllQuizzes({
                    pages: response.data.data.pagesAmount,
                    currentPage: response.data.data.currentPage,
                    quizzes: response.data.data.allQuizzes
                });
                setSimpleLoading(false);
            })
        }
        catch (err) {
            console.log(err);
            general.toast.error(translations[currentLanguage].error);
            setSimpleLoading(false);
        }
    }

    useEffect(() => {
        setInputForAi('');
        if (page === 'studySession' || page == 'main') {
            clearAll();
        }
        if (page == 'studySession') {
            general.api.post('/study/session/create').then((response) => {
                if (response.data.status !== 200) {
                    general.toast.error(translations[currentLanguage].error);
                    return;
                }
                setSessionId(response.data.data._id);
                setSession(response.data.data);
                setStudyTime(0);
            }
            ).catch((err) => {
                console.log(err);
                general.toast.error(translations[currentLanguage].error);
            }
            ).finally(() => {
                setLoading(false);
            });
        }
        if (general.checkAuth()) {
            setTimeout(() => {
                setCurrentLanguage(general?.getAccount()?.preferredLanguage ?? 'EN');
            }, 150);
        }
        else {
            general.logout();
        }
        if (curiosities?.length == 0) {
            let choosenIndexes = general.multipleRandomNumbers(0, 24, 5, false);
            setCuriosities(studyTips[currentLanguage ?? 'EN'].filter((_, index) => choosenIndexes.includes(index)).map((item) => item));
        }
    }, [page]);

    useEffect(() => {
        if (general.checkAuth()) {
            setTimeout(() => {
                setCurrentLanguage(general?.getAccount()?.preferredLanguage ?? 'EN');
            }, 150);
        }
        else {
            general.logout();
        }
    }, [choosingOptions]);

    const actionOptions = {
        main: {
            title: translations[currentLanguage].mainPageTitle1,
            options: [
                {
                    icon: general.icons.faBookOpen,
                    text: translations[currentLanguage].startStudySession,
                    function: () => {
                        setPage('studySession')
                        setChoosingOptions(false);
                    }
                },
                {
                    icon: general.icons.faClipboardList,
                    text: translations[currentLanguage].myQuizzes,
                    function: () => {
                        setPage('quizHistory');
                        setChoosingOptions(false);
                    }
                },
                {
                    icon: general.icons.faNoteSticky,
                    text: translations[currentLanguage].flashcards,
                    function: () => {
                        setPage('flashcardsHistory');
                        setChoosingOptions(false);
                    }
                },
                {
                    icon: general.icons.faHistory,
                    text: translations[currentLanguage].myHistory,
                    function: () => {
                        setPage('myHistory')
                        setChoosingOptions(false);
                    }
                },
                {
                    icon: general.icons.faUser,
                    text: translations[currentLanguage].myAccount,
                    function: () => {
                        setPage('myAccount');
                        setChoosingOptions(true);
                    }
                },
            ]
        },
        myAccount: {
            title: translations[currentLanguage].myAccount,
            options: [
                {
                    icon: general.icons.faArrowLeft,
                    text: translations[currentLanguage].return,
                    function: () => {
                        setPage('main');
                        setChoosingOptions(true);
                    }
                },
                {
                    icon: general.icons.faUserEdit,
                    text: translations[currentLanguage].editInfo,
                    function: () => {
                        general.toast.info(translations[currentLanguage].comingSoon);
                    }
                },
                {
                    icon: general.icons.faLanguage,
                    text: translations[currentLanguage].currentLanguage + general.languages.find((lang) => lang.code === currentLanguage)?.name,
                    function: () => {
                        setCurrentLanguage(general.languages.findIndex((lang) => lang.code === currentLanguage) === allLanguageCodes.length - 1 ? allLanguageCodes[0] : allLanguageCodes[general.languages.findIndex((lang) => lang.code === currentLanguage) + 1])
                        general.api.post('/account/update/' + general.getAccount()._id, { preferredLanguage: general.languages.findIndex((lang) => lang.code === currentLanguage) === allLanguageCodes.length - 1 ? allLanguageCodes[0] : allLanguageCodes[general.languages.findIndex((lang) => lang.code === currentLanguage) + 1] }).then((response) => {
                            general.checkAuth();
                        }).catch((err) => {
                            console.log(err);
                            general.toast.error('Erro ao atualizar idioma.');
                        });
                    }
                },
                {
                    icon: general.icons.faSignOutAlt,
                    text: translations[currentLanguage].leave,
                    function: () => {
                        general.logout();
                    }
                },
                {
                    icon: general.icons.faCrown,
                    text: (!general.getAccount()?.accountType || general.getAccount()?.accountType === 'free') ? translations[currentLanguage].upgradeToPremiumTitle : translations[currentLanguage].manageSubscription,
                    function: () => {
                        if (!general.getAccount()?.accountType || general.getAccount()?.accountType === 'free') {
                            setPremiumPopUpActive(true);
                        }
                        else {
                            getStripePortalSession();
                        }
                    }
                },
            ]
        },
        myHistory: {
            title: translations[currentLanguage].myHistory,
            options: []
        }
    }

    const getStudySession = async (id: string) => {
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/study/session/get/' + id, {
                sessionId: id,
            });
            if (response.data.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setSession(response.data.data);
            setSessionId(response.data.data._id);
            setStudyTime(response.data.data.studyTime);
            setCuriosities((currentCuriosities) => [...currentCuriosities, ...response.data.data.topics[response.data.data.topics.length - 1]?.curiosities ?? []]);
            setSessionSearchedTopics(response.data.data.topics.map((topic: any) => topic.name));
            setCurrentTopic(response.data.data.topics[response.data.data.topics.length - 1]);
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const getQuiz = async (id: string) => {
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/quiz/get/' + id, {
                sessionId: id,
            });
            if (response.data.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setQuiz(response.data.data.quiz);
            setSession(response.data.data.session);
            setSessionId(response.data.data.session._id);
            setStudyTime(response.data.data.session.studyTime);
            setCuriosities((currentCuriosities) => [...currentCuriosities, ...response.data.data.session.topics[response.data.data.session.topics.length - 1]?.curiosities ?? []]);
            setSessionSearchedTopics(response.data.data.session.topics.map((topic: any) => topic.name));
            setCurrentTopic(response.data.data.session.topics[response.data.data.session.topics.length - 1]);
            setCurrentQuizQuestionIndex(0);
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const retryQuiz = async (id: string) => {
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/quiz/retry/' + id, {
                sessionId: sessionId,
            });
            if (response.data.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setQuiz(response.data.data.quiz);
            setCurrentQuizQuestionIndex(0);
            setPage('takingQuiz');
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const setQuizAnswer = (answer: string) => {
        try {
            if (quiz?.questions[currentQuizQuestionIndex]?.answers?.filter((ans: any) => ans.answer === answer)[0]?.isCorrect) {
                setQuiz((oldQuiz: any) => {
                    let newQuiz = { ...oldQuiz };
                    newQuiz.questions[currentQuizQuestionIndex].studentAnswer = answer;
                    newQuiz.questions[currentQuizQuestionIndex].isCorrect = true;
                    return newQuiz;
                });
            }
            else {
                setQuiz((oldQuiz: any) => {
                    let newQuiz = { ...oldQuiz };
                    newQuiz.questions[currentQuizQuestionIndex].studentAnswer = answer;
                    newQuiz.questions[currentQuizQuestionIndex].isCorrect = false;
                    return newQuiz;
                });
            }
        }
        catch (err) {
            console.log(err);
            general.toast.error('Erro ao realizar processo.');
        }
    }



    const getFlashcards = async (id: string) => {
        setSimpleLoading(true);
        try {
            const response = await general.api.post('/flashcards/get/' + id, {
                sessionId: id,
            });
            if (response.data.status !== 200) {
                general.toast.error('Erro ao realizar processo.');
                console.error(response);
                setSimpleLoading(false);
                return;
            }
            setFlashcards(response.data.data.flashcards);
            setSession(response.data.data.session);
            setSessionId(response.data.data.session._id);
            setStudyTime(response.data.data.session.studyTime);
            setCuriosities((currentCuriosities) => [...currentCuriosities, ...response.data.data.session.topics[response.data.data.session.topics.length - 1]?.curiosities ?? []]);
            setSessionSearchedTopics(response.data.data.session.topics.map((topic: any) => topic.name));
            setCurrentTopic(response.data.data.session.topics[response.data.data.session.topics.length - 1]);
            setCurrentQuizQuestionIndex(0);
            setSimpleLoading(false);
        }
        catch (err) {
            console.log(err);
            setSimpleLoading(false);
            general.toast.error('Erro ao realizar processo.');
        }
    }

    const updateAllFlashcards = (page: number) => {
        setSimpleLoading(true);
        try {
            general.api.post('/flashcards/list', {
                currentPage: page,
                pageSize: 15,
            }).then((response) => {
                if (response.data.status !== 200) {
                    general.toast.error(translations[currentLanguage].error);
                    setSimpleLoading(false);
                    return;
                }
                setAllFlashcards({
                    pages: response.data.data.pagesAmount,
                    currentPage: response.data.data.currentPage,
                    flashcards: response.data.data.allFlashcards
                });
                setSimpleLoading(false);
            })
        }
        catch (err) {
            console.log(err);
            general.toast.error(translations[currentLanguage].error);
            setSimpleLoading(false);
        }
    }

    /* Stripe Requests */

    const getStripePortalSession = async () => {
        try {
            setRedirectLoading(true);
            const response = await general.api.post('/stripe/create/portal').then(res => {
                if (res.data.status === 200) {
                    window.location.href = res.data.data.url;
                } else {
                    console.error('Error creating checkout session:', res.data.message);
                    setRedirectLoading(false);
                    general.toast.error(translations[currentLanguage].error);
                }
            });
        } catch (error) {
            console.error('Error fetching checkout session:', error);
            setRedirectLoading(false);
            throw error;
        }
    }

    /* End Stripe Requests */

    /* useEffects */
    useEffect(() => {
        document.getElementById('learnTopicOutput')?.scroll(0, 0);
        if (sessionSearchedTopics?.length == 0) {
            setStudyTime(0)
            studyTimeRef.current = 0;
        }
        if (currentTopic) {
            general.api.post('/youtube/search', {
                search: currentTopic?.name,
            }).then((response) => {
                if (response.data.status !== 200) {
                    general.toast.error(translations[currentLanguage].error);
                    return;
                }
                setYoutubeVideos(response.data.data.videos);
                setCurrentYoutubeVideo(response.data.data.videos[0]);
            }).catch((err) => {
                console.log(err);
                general.toast.error(translations[currentLanguage].error);
            })
        }
    }, [currentTopic?._id]);

    useEffect(() => {
        setShowSections({
            terms: true,
            relatedTopics: true,
            sessionTopics: true,
            quizzes: true,
            flashcards: true
        });
        setSelectedTopics([]);
        const changeStudyTimeInterval = setInterval(() => {
            if (page === 'topicAnswer') {
                setStudyTime(oldStudyTime => oldStudyTime + 500);
                studyTimeRef.current = studyTimeRef.current + 500;
            }
        }, 500);

        const updateStudyTime = setInterval(() => {
            if (page === 'topicAnswer') {
                if (session) {
                    general.api.post('/study/session/update/' + sessionId, {
                        studyTime: studyTimeRef.current
                    }).then((response) => {
                        if (response.data.status !== 200) {
                            general.toast.error(translations[currentLanguage].error);
                            console.error(response);
                            return;
                        }
                        setSession(response.data.data);
                    }).catch((err) => {
                        console.log(err);
                        general.toast.error(translations[currentLanguage].error);
                    });
                }
            }
        }, 5000);

        if (page === 'myHistory') {
            updateAllSessions(allSessions.currentPage);
        }

        if (page === 'quizHistory') {
            updateAllQuizzes(allQuizzes.currentPage);
        }

        if (page === 'flashcardsHistory') {
            updateAllFlashcards(allFlashcards.currentPage);
        }

        return () => {
            clearInterval(changeStudyTimeInterval)
            clearInterval(updateStudyTime);
        };
    }, [page, sessionId]);

    useEffect(() => {
        studyTimeRef.current = session?.studyTime;
    }, [sessionId]);

    /* End UseEffects */

    return {
        general,
        currentLanguage,
        setCurrentLanguage,
        page,
        setPage,
        actionOptions,
        choosingOptions,
        setChoosingOptions,
        inputForAi,
        setInputForAi,
        topicLearn,
        loading,
        setLoading,
        sessionSearchedTopics,
        setSessionSearchedTopics,
        betterExplaining,
        setBetterExplaining,
        betterExplainingActive,
        setBetterExplainingActive,
        betterExplainTerm,
        betterExplainingTerm,
        setBetterExplainingTerm,
        curiosities,
        setCuriosities,
        getTopicsTips,
        simpleLoading,
        setSimpleLoading,
        studyTime,
        setStudyTime,
        session,
        setSession,
        sessionId,
        setSessionId,
        currentTopic,
        setCurrentTopic,
        clearAll,
        allSessions,
        setAllSessions,
        updateAllSessions,
        allLanguageCodes,
        youtubeVideos,
        setYoutubeVideos,
        currentYoutubeVideo,
        setCurrentYoutubeVideo,
        youtubeVideoModal,
        setYoutubeVideoModal,
        getStudySession,
        showSections,
        setShowSections,
        selectedTopics,
        setSelectedTopics,
        createQuiz,
        quiz,
        setQuiz,
        currentQuizQuestionIndex,
        setCurrentQuizQuestionIndex,
        retryQuiz,
        setQuizAnswer,
        allQuizzes,
        setAllQuizzes,
        updateAllQuizzes,
        getQuiz,
        createFlashcards,
        flashcards,
        setFlashcards,
        getFlashcards,
        updateAllFlashcards,
        allFlashcards,
        setAllFlashcards,
        premiumPopUpActive,
        setPremiumPopUpActive,
        getStripePortalSession,
        redirectLoading,
        setRedirectLoading,
        buttonRedirectLoading,
        setButtonRedirectLoading,
    }
}

export default useController;
